import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Switch } from "@tql/ui-components-react/kendo/inputs";
import { useEffect, useRef, useState } from "react";

import { label } from "./index.module.css";

type SwitchCellProps<T> = Omit<GridCustomCellProps, "onChange"> & {
	data: T;
	ariaLabel?: string;
	checked: boolean;
	dataTestId?: string;
	onChange?: (value: boolean) => false | void | Promise<void>;
};

const handleClick = (e: React.MouseEvent) => {
	e.stopPropagation();
};

export const SwitchCell = <T,>({
	data,
	ariaLabel,
	checked: initialChecked,
	dataTestId,
	onChange,
	...gridProps
}: SwitchCellProps<T>) => {
	// Guard clause: if no dataItem, render empty cell
	const [checked, setChecked] = useState(initialChecked);
	const [isPending, setIsPending] = useState(false);
	const isPendingRef = useRef(false);

	useEffect(() => {
		setChecked(initialChecked);
	}, [initialChecked]);

	if (!data) {
		return <td {...gridProps.tdProps} />;
	}

	return (
		<td {...gridProps.tdProps} onClick={handleClick}>
			<Switch
				ariaLabel={ariaLabel}
				checked={checked}
				data-testid={dataTestId}
				disabled={isPending}
				onChange={(e) => {
					if (isPendingRef.current) return;
					const accepted = onChange?.(e.value);
					if (accepted === false) return;

					setChecked(e.value);
					if (!accepted) return;

					isPendingRef.current = true;
					setIsPending(true);
					void accepted
						.catch(() => undefined)
						.finally(() => {
							isPendingRef.current = false;
							setIsPending(false);
						});
				}}
			/>
			<span className={label}>{checked ? "On" : "Off"}</span>
		</td>
	);
};
